import type { RecorderErrorKind } from "@/lib/voice-order";
import type { RecorderStatus } from "@/hooks/useAudioRecorder";

import {
  VoiceRecorderChrome,
  type VoiceRecorderChromeCopy
} from "./VoiceRecorderChrome";
import { VoiceRecorderFailureCard } from "./VoiceRecorderFailureCard";

interface VoiceMessageRecorderPanelProps {
  status: RecorderStatus;
  error: RecorderErrorKind | null;
  remainingMs: number;
  onStart: () => void;
  onStop: () => void;
  /** Abandon the flow instead of retrying - there is no catalog to fall back
   *  to here, unlike the order flow. */
  onCancel: () => void;
}

/**
 * Shorter than the order flow's `ERROR_COPY` - a voice message has no catalog
 * fallback, so every body drops the "or search the catalog" tail and the
 * recovery action is "Cancel" instead of "Search the catalog" (KAN-192).
 */
const MESSAGE_ERROR_COPY: Record<
  RecorderErrorKind,
  { title: string; body: string; canRetry: boolean }
> = {
  "permission-denied": {
    title: "Microphone access needed",
    body: "We need permission to use your microphone to record your message. Allow it in your browser, then try again.",
    canRetry: true
  },
  "no-microphone": {
    title: "No microphone found",
    body: "We couldn't find a microphone on this device. Connect one and try again.",
    canRetry: true
  },
  "recording-unavailable": {
    title: "Couldn't start recording",
    body: "Your microphone couldn't be started - it may be in use by another app. Close that and try again.",
    canRetry: true
  },
  unsupported: {
    title: "Voice messages aren't supported here",
    body: "This browser can't record audio. Try a recent Chrome or Safari.",
    canRetry: false
  },
  unknown: {
    title: "Something went wrong",
    body: "We couldn't start recording. Please try again.",
    canRetry: true
  }
};

const RECORDER_COPY: VoiceRecorderChromeCopy = {
  idleTitle: "Tap and record your message",
  idleDescription:
    "Leave a short message for our team - we'll get back to you.",
  requestingTitle: "Waiting for microphone…",
  requestingDescription: "Allow microphone access to continue.",
  recordingTitle: "Recording…",
  recordingHint: "Tap to finish, or it stops automatically at 60s."
};

/**
 * Stage 0 of the "leave a voice message" flow (KAN-192): capture the message.
 * Mirrors `VoiceRecorderPanel`'s three-state shape (failure / recording /
 * idle) on the same shared `VoiceRecorderChrome` and `VoiceRecorderFailureCard`,
 * but with its own short error copy and a "Cancel" recovery instead of a
 * catalog fallback - there is nothing to search here.
 */
export function VoiceMessageRecorderPanel({
  status,
  error,
  remainingMs,
  onStart,
  onStop,
  onCancel
}: VoiceMessageRecorderPanelProps) {
  if (error) {
    return (
      <VoiceRecorderFailureCard
        error={error}
        copy={MESSAGE_ERROR_COPY[error]}
        onRetry={onStart}
        fallbackLabel="Cancel"
        onFallback={onCancel}
      />
    );
  }

  return (
    <VoiceRecorderChrome
      status={status}
      remainingMs={remainingMs}
      onStart={onStart}
      onStop={onStop}
      copy={RECORDER_COPY}
    />
  );
}
